import React, { useState, useEffect } from 'react';
import { Route, MapPin, DollarSign, Truck } from 'lucide-react';
import Input from '../../components/forms/Input';
import Button from '../../components/ui/Button';
import apiService from '../../services/apiService';

const RangoFleteForm = ({ rango, onSubmit, onCancel, isLoading, isEdit = false }) => {
    const [formData, setFormData] = useState({
        distancia_desde: '',
        distancia_hasta: '',
        vehiculo_id: '',
        costo: '',
        activo: 1
    });

    const [errors, setErrors] = useState({});
    const [vehiculos, setVehiculos] = useState([]);

    useEffect(() => {
        loadVehiculos();
    }, []);

    useEffect(() => {
        if (isEdit && rango) {
            setFormData({
                distancia_desde: rango.distancia_desde?.toString() || '',
                distancia_hasta: rango.distancia_hasta?.toString() || '',
                vehiculo_id: rango.vehiculo_id?.toString() || '',
                costo: rango.costo?.toString() || '',
                activo: rango.activo ? 1 : 0
            });
        }
    }, [isEdit, rango]);

    const loadVehiculos = async () => {
        try {
            const result = await apiService.getVehiculos();
            if (result.success) {
                setVehiculos(result.data.filter(v => v.activo));
            }
        } catch (error) {
            console.error('Error al cargar vehículos:', error);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpiar error del campo
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.distancia_hasta || parseFloat(formData.distancia_hasta) <= 0) {
            newErrors.distancia_hasta = 'La distancia hasta debe ser mayor a 0';
        }

        if (!formData.costo || parseFloat(formData.costo) <= 0) {
            newErrors.costo = 'El costo debe ser mayor a 0';
        }

        const distanciaDesde = parseFloat(formData.distancia_desde) || 0;
        const distanciaHasta = parseFloat(formData.distancia_hasta) || 0;

        if (distanciaDesde >= distanciaHasta) {
            newErrors.distancia_hasta = 'La distancia hasta debe ser mayor a la distancia desde';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const dataToSubmit = {
            distancia_desde: parseFloat(formData.distancia_desde) || 0,
            distancia_hasta: parseFloat(formData.distancia_hasta),
            vehiculo_id: formData.vehiculo_id && formData.vehiculo_id !== '' ? parseInt(formData.vehiculo_id) : null,
            costo: parseFloat(formData.costo),
            activo: parseInt(formData.activo)
        };

        onSubmit(dataToSubmit);
    };

    return (
        <form onSubmit={handleSubmit} className="rango-flete-form">
            <div className="form-container">
                <div className="form-column">
                    <h3 className="column-title">
                        <Route size={18} />
                        Configuración del Rango
                    </h3>

                    <div className="form-row">
                        <div className="form-group">
                            <Input
                                label="Distancia Desde (km)"
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.distancia_desde}
                                onChange={(e) => handleInputChange('distancia_desde', e.target.value)}
                                error={errors.distancia_desde}
                                icon={<MapPin size={18} />}
                                placeholder="0.00"
                                disabled={isLoading}
                            />
                        </div>

                        <div className="form-group">
                            <Input
                                label="Distancia Hasta (km)"
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={formData.distancia_hasta}
                                onChange={(e) => handleInputChange('distancia_hasta', e.target.value)}
                                error={errors.distancia_hasta}
                                icon={<MapPin size={18} />}
                                placeholder="5.00"
                                required
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Vehículo Asignado</label>
                        <select
                            value={formData.vehiculo_id}
                            onChange={(e) => handleInputChange('vehiculo_id', e.target.value)}
                            className="form-select"
                            disabled={isLoading}
                        >
                            <option value="">Sin vehículo asignado</option>
                            {vehiculos.map(vehiculo => (
                                <option key={vehiculo.id} value={vehiculo.id}>
                                    {vehiculo.nombre} - {vehiculo.placa}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <Input
                            label="Costo del Flete"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.costo}
                            onChange={(e) => handleInputChange('costo', e.target.value)}
                            error={errors.costo}
                            icon={<DollarSign size={18} />}
                            placeholder="25.00"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {isEdit && (
                        <div className="form-group">
                            <label className="form-label">Estado</label>
                            <select
                                value={formData.activo}
                                onChange={(e) => handleInputChange('activo', e.target.value)}
                                className="form-select"
                                disabled={isLoading}
                            >
                                <option value={1}>✅ Activo</option>
                                <option value={0}>❌ Inactivo</option>
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Botones de acción */}
            <div className="form-actions">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Cancelar
                </Button>

                <Button
                    type="submit"
                    loading={isLoading}
                    disabled={isLoading}
                >
                    {isEdit ? 'Actualizar Rango' : 'Crear Rango'}
                </Button>
            </div>
        </form>
    );
};

export default RangoFleteForm;